import _ from 'lodash';
import { useEffect, useState } from 'react';
import {
  handleResetStudy as handleResetStudyMutation,
  handleStopHolterStudyProcessing,
} from '@/entities/study';
import { EMITTER_CONSTANTS, LOADING_TYPES, URL_CALLCENTER_PORTAL } from '@/shared/config';

import { IReportInfo } from '@/shared/model';
import { deleteStudyMetadata } from '@/shared/lib';
import emitter from '@/shared/lib/eventEmitter';
import { useLoadingStore } from '@/shared/lib/loadingStore';
import { toastrError } from '@/shared/lib/toastNotification';
import { useReportStore } from '@/entities/report';
import type { ReportHeaderProps } from './index';

/**
 * Backs the report header with study identity, its action menu and the save shortcut. Reads reportInfo from the store, works out whether reset-study must be disabled because the profile is still pending without AI processing started, tracks popover and modal state, measures the facility label for ellipsis, flags a multi-user conflict, and binds a window keydown listener for Ctrl+S saving.
 *
 * @param props - Header props carrying studyId, profileId, activeTab, conflictUsers, pendingProfiles, onClickSaveReport and the isDisableSaveReport, isArtifactReport, isStartedAiProcessing and isLoadingSaveAiReport flags.
 * @returns An object carrying reportInfo, shouldDisableResetStudyByProcessingStatus, isEllipsisActive, isOpenStopProcessingModal, isOpenPopover, isOpenResetStudyModal, setIsOpenResetStudyModal, isResetingStudy, isMutiUserConflict and the handleOpenChange, onClickCloseTab, onClickResetStudy, handleResetStudy and handleStopAiProcessing handlers.
 */
const useReportHeader = ({
  isDisableSaveReport,
  onClickSaveReport,
  profileId,
  studyId,
  isArtifactReport,
  isStartedAiProcessing,
  activeTab,
  isLoadingSaveAiReport,
  pendingProfiles = [],
  conflictUsers,
}: Pick<
  ReportHeaderProps,
  | 'isDisableSaveReport'
  | 'onClickSaveReport'
  | 'profileId'
  | 'studyId'
  | 'isArtifactReport'
  | 'isStartedAiProcessing'
  | 'activeTab'
  | 'isLoadingSaveAiReport'
  | 'pendingProfiles'
  | 'conflictUsers'
>) => {
  const loading = useLoadingStore((s) => s.loading);
  const loadingPageAction = useLoadingStore((s) => s.loadingPage);

  // IReportInfo (Recoil/model.ts) doesn't declare isNonPhi - pre-existing gap.
  const reportInfo = useReportStore((s) => s.reportInfo) as IReportInfo & { isNonPhi?: boolean };
  const { studyFid, patientName, facilityName, reportFid, isNonPhi } = reportInfo;

  // Check if current profile is in pendingProfiles and isStartedAiProcessing is false
  const shouldDisableResetStudyByProcessingStatus = _.some(
    pendingProfiles,
    (profile) => profile?.id === profileId && !isStartedAiProcessing,
  );

  const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  const [isOpenStopProcessingModal, setIsOpenStopProcessingModal] = useState(false);
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const [isOpenResetStudyModal, setIsOpenResetStudyModal] = useState(false);
  const [isResetingStudy, setIsResetingStudy] = useState(false);
  const [isMutiUserConflict, setIsMutiUserConflict] = useState(false);

  /**
   * Drives the open state of the report header's action popover. Writes the requested state straight to isOpenPopover so the popover can also close itself on outside clicks.
   *
   * @param newOpen - Whether the popover should now be visible.
   * @returns Nothing.
   */
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpenPopover(newOpen);
  };

  /**
   * Closes the report header's action popover. Sets isOpenPopover to false.
   *
   * @returns Nothing.
   */
  const hidePopover = () => {
    setIsOpenPopover(false);
  };

  /**
   * Leaves the report and returns to the study's page in the call centre portal. Redirects document.location.href to the portal studies URL for the current studyId, discarding the report tab.
   *
   * @returns Nothing.
   */
  const onClickCloseTab = () => {
    document.location.href = `${URL_CALLCENTER_PORTAL}studies/${studyId}`;
  };

  /**
   * Starts the reset-study flow from the header popover. Closes the popover first, then opens the reset confirmation modal.
   *
   * @returns Nothing.
   */
  const onClickResetStudy = () => {
    hidePopover();
    setIsOpenResetStudyModal(true);
  };

  /**
   * Resets the study on the server and closes the report tab. Flags the window as stopping AI processing, runs the reset mutation for the current study and profile, deletes the cached study metadata by studyFid, then redirects to the portal; on failure it toasts the error and rolls the resetting flag back.
   *
   * @returns A promise resolving once the study is reset and the tab redirected, or the failure has been reported.
   */
  const handleResetStudy = async () => {
    setIsResetingStudy(true);
    window.isStopAiProcess = true;
    try {
      const input = {
        studyId,
        profileId,
      };
      await handleResetStudyMutation(input);
      // pre-existing bug: studyFid is typed number (IReportInfo) but deleteStudyMetadata expects a string key.
      await deleteStudyMetadata(studyFid as unknown as string);
      onClickCloseTab();
    } catch (error) {
      toastrError(error.message, 'Error');
      setIsResetingStudy(false);
      window.isStopAiProcess = undefined;
    }
  };

  /**
   * Halts the running AI processing for the current Holter study. Shows the stopping-AI loading page, closes the popover, flags the window, calls the stop-processing mutation, deletes the cached study metadata by studyFid, opens the stop-processing modal and emits STOP_AI_PROCESSING; errors are toasted and the window flag cleared, and the loading page is dismissed either way.
   *
   * @returns A promise resolving once processing has been stopped and the loading page dismissed.
   */
  const handleStopAiProcessing = async () => {
    loadingPageAction(LOADING_TYPES.STOPPING_AI_PROCESS);
    hidePopover();
    window.isStopAiProcess = true;
    try {
      const input = {
        studyId,
        profileId,
      };
      await handleStopHolterStudyProcessing(input);
      // pre-existing bug: studyFid is typed number (IReportInfo) but deleteStudyMetadata expects a string key.
      await deleteStudyMetadata(studyFid as unknown as string);
      setIsOpenStopProcessingModal(true);

      emitter.emit(EMITTER_CONSTANTS.STOP_AI_PROCESSING);
    } catch (error) {
      toastrError(error.message, 'Error');
      window.isStopAiProcess = undefined;
    }
    loadingPageAction();
  };

  useEffect(() => {
    if (!_.isEmpty(reportInfo)) {
      const elBtn = document.getElementById('headerFacilityLabel');
      if (elBtn) {
        const elText = elBtn.querySelector('.name') as HTMLElement;
        if (elText.offsetWidth < elText.scrollWidth) {
          setIsEllipsisActive(true);
        }
      }
    }
  }, [reportInfo]);

  useEffect(() => {
    setIsMutiUserConflict(conflictUsers?.length > 1);
  }, [conflictUsers]);

  useEffect(() => {
    /**
     * Provides the Ctrl+S and Cmd+S shortcut for saving the report. Ignores auto-repeat and any state where saving is unavailable — save disabled, an artifact report, a loading text showing, or an AI report save in flight — then cancels the browser's own save dialog and triggers onClickSaveReport.
     *
     * @param event - Keyboard event whose keyCode, ctrlKey, metaKey and repeat flags decide whether the save shortcut fires.
     * @returns Nothing.
     */
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event?.repeat ||
        isDisableSaveReport ||
        isArtifactReport ||
        !!loading.loadingText ||
        isLoadingSaveAiReport
      ) {
        return;
      }
      const { keyCode, ctrlKey, metaKey } = event;
      //* Ctrl + S
      if ((ctrlKey || metaKey) && keyCode === 83) {
        event.preventDefault();
        onClickSaveReport();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    onClickSaveReport,
    activeTab,
    isDisableSaveReport,
    isArtifactReport,
    loading.loadingText,
    isLoadingSaveAiReport,
  ]);

  return {
    reportInfo,
    shouldDisableResetStudyByProcessingStatus,
    isEllipsisActive,
    isOpenStopProcessingModal,
    isOpenPopover,
    isOpenResetStudyModal,
    setIsOpenResetStudyModal,
    isResetingStudy,
    isMutiUserConflict,
    handleOpenChange,
    onClickCloseTab,
    onClickResetStudy,
    handleResetStudy,
    handleStopAiProcessing,
  };
};

export default useReportHeader;
